import { useTranslation } from 'react-i18next';
import ArchiveRestrictedModal from 'components/ArchiveRestrictedModal/ArchiveRestrictedModal';
import DataSetsEmptyList from 'components/DataSetsEmptyList/DataSetsEmptyList';
import DataSetsItems from 'components/DataSetsItems/DataSetsItems';
import DataSetsListFilters from 'components/DataSetsListFilters/DataSetsListFilters';
import DeactivationInfoModal from 'components/DeactivationModals/DeactivationInfoModal';
import DeactivationModal from 'components/DeactivationModals/DeactivationModal';
import EmptyList from 'components/EmptyList/EmptyList';
import ImportErrorsModal from 'components/ImportErrorsModal/ImportErrorsModal';
import MappingModal from 'components/MappingModal/MappingModal';
import Pagination from 'components/Pagination/Pagination';
import Panel from 'components/Panel/Panel';
import QuestionErrorModal from 'components/QuestionErrorModal/QuestionErrorModal';
import { DATA_SET_PAGINATION_COUNT, DATA_SETS_PROFIT_CENTRES_HEAD_CELLS } from 'constants/dataSets';
import { DELETE_MODAL_TITLES } from 'constants/interfaces';
import DeleteModal from 'pages/CompanyAdminPages/ValueChainMap/components/DeleteModal/DeleteModal';

import ProfitCentresItem from './components/ProfitCentresItem/ProfitCentresItem';

import type { Control } from 'react-hook-form';
import type { TDataSetsListSchema } from 'utils/validators';
import type IProfitCentresView from './interfaces/IProfitCentresView';
import type { TUseProfitCenterImportRecordsMutation } from './interfaces/TUseProfitCenterImportRecordsMutation';

import styles from './sass/ProfitCentresView.module.scss';

function ProfitCentresView({
  isEmptyList, uploadControl, isActionsVisible, onDownloadExample, isMappingModalVisible, onCloseMappingModal,
  isImportErrorsModalVisible, setIsImportErrorsModalVisible, control,
  meta, items, onSortChange, onSearchChange, onSelectAll, isLineMark, onArchive, setIsArchiveModalVisible,
  onArchiveConfirm, isArchiveModalVisible, isArchiveSelectedItemsVisible, isArchiveModalInfoVisible,
  setIsArchiveModalInfoVisible, latestArchivation, onArchiveModalInfoOpen, onMoveToArchive, onDeArchive, selectedItems,
  onPageChange, currentTotalItems, onEditRecord, importRecords, onAddRecord, isWithSearch, isLoading,
  isLazyProfitCenterSheetLoading, onShowArchiveChange, activeModal, closeModal, archiveError,
  locationsCountries, onDeleteRecord, isDeleteModalVisible, onCloseDeleteModal, onDeleteConfirm,
  isImportErrorModalVisible, importErrorMessage, onCloseImportErrorModal, onImportError,
}: IProfitCentresView) {
  const { t } = useTranslation();

  return (
    <>
      {!isLoading && !isLazyProfitCenterSheetLoading && isEmptyList && !isWithSearch ? (
        <DataSetsEmptyList
          title={t('profitCentres.title')}
          subTitle={t('profitCentres.subTitle')}
          uploadControl={uploadControl}
          isUploadFileVisible={isActionsVisible}
          onDownloadExample={onDownloadExample}
          onShowArchiveChange={onShowArchiveChange}
          control={control}
          titleToggle={t('dataSetsManagement.showArchived')}
          onAddRecord={onAddRecord}
        />
      ) : null}
      {!isEmptyList || isWithSearch ? (
        <div className={styles.root}>
          <Panel className={styles.panel}>
            <div className={styles.content}>
              <DataSetsListFilters
                control={control}
                meta={meta}
                onSearchChange={onSearchChange}
                onArchive={onArchive}
                isArchiveSelectedItemsVisible={isArchiveSelectedItemsVisible}
                selectedItems={selectedItems}
                currentTotalItems={currentTotalItems}
                isActionsVisible={isActionsVisible}
                title={t('profitCentres.title')}
                subTitle={t('profitCentres.subTitle')}
                onAddRecord={onAddRecord}
                onShowArchiveChange={onShowArchiveChange}
                onDownloadExample={onDownloadExample}
                uploadControl={uploadControl}
              />
              {isEmptyList && isWithSearch && !isLoading && !isLazyProfitCenterSheetLoading ? <EmptyList /> : null}
              {!isEmptyList && (
                <>
                  <DataSetsItems<Control<TDataSetsListSchema>>
                    control={control}
                    onSortChange={onSortChange}
                    onSelectAll={onSelectAll}
                    isLineMark={isLineMark}
                    isActionsVisible={isActionsVisible}
                    headCells={DATA_SETS_PROFIT_CENTRES_HEAD_CELLS}
                    disabledCheckbox={!items?.some((item) => !item.archived)}
                  >
                    {!isLoading && items?.map((item) => (
                      <ProfitCentresItem
                        key={item.id}
                        item={item}
                        control={control}
                        onArchiveModalInfoOpen={onArchiveModalInfoOpen}
                        onMoveToArchive={onMoveToArchive}
                        onDeArchive={onDeArchive}
                        onEditRecord={onEditRecord}
                        isActionsVisible={isActionsVisible}
                        locationsCountries={locationsCountries}
                        onDeleteRecord={onDeleteRecord}
                      />
                    ))}
                  </DataSetsItems>
                  <Pagination
                    count={meta?.count || 0}
                    offset={meta?.offset || 0}
                    limit={DATA_SET_PAGINATION_COUNT}
                    onPageChange={onPageChange}
                  />
                </>
              )}
            </div>
          </Panel>
        </div>
      ) : null}
      <MappingModal<TUseProfitCenterImportRecordsMutation>
        isMappingModalVisible={isMappingModalVisible}
        onCloseMappingModal={onCloseMappingModal}
        setIsImportErrorsModalVisible={setIsImportErrorsModalVisible}
        importRecords={importRecords}
        isLoading={isLoading}
        onImportError={onImportError}
      />
      <ImportErrorsModal
        isImportErrorsModalVisible={isImportErrorsModalVisible}
        setIsImportErrorsModalVisible={setIsImportErrorsModalVisible}
        isLoading={isLoading}
        importRecords={importRecords}
        onImportError={onImportError}
      />
      <QuestionErrorModal
        isOpen={isImportErrorModalVisible}
        onClose={onCloseImportErrorModal}
        title={t('profitCentres.title')}
        errorLog={importErrorMessage ? t('dataSetsManagement.errorMessage') : undefined}
        hideRecommendations
      />
      <DeactivationModal
        open={isArchiveModalVisible}
        title={t('profitCentres.archivingReasonTitle')}
        text={t('profitCentres.archivingReasonDescription')}
        onReject={() => { setIsArchiveModalVisible(false); }}
        onSubmit={onArchiveConfirm}
        isLoading={isLoading}
      />
      <DeactivationInfoModal
        open={isArchiveModalInfoVisible}
        title={t('profitCentres.archiveDetails')}
        deactivationInfo={latestArchivation}
        onClose={() => setIsArchiveModalInfoVisible(false)}
        isLoading={isLoading}
      />
      <ArchiveRestrictedModal
        isOpen={activeModal}
        onClose={closeModal}
        archiveError={archiveError}
      />
      <DeleteModal
        open={isDeleteModalVisible}
        onReject={onCloseDeleteModal}
        isLoading={false}
        name=""
        title={DELETE_MODAL_TITLES.DELETE_RECORD}
        onConfirm={onDeleteConfirm}
      />
    </>
  );
}

export default ProfitCentresView;
